import { type ChangeEvent } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { TenantEnum } from '@jsmdg/tenancy';
import {
    CheckboxRadioAlign,
    GiftWrappingIcon,
    Link,
    LinkVariant,
    PdfIcon,
    RadioButton,
} from '@jsmdg/yoshi';
import { ProductType } from '../../../../shared/enums';
import { useRedemptionCart } from '../../../hooks/useRedemptionCart';
import { type ProductDetailContext } from '../../../types/types';
import { trackProductItemClicked } from './productGroupItem.tracking';
import styles from './ProductGroupItem.module.scss';

const messages = defineMessages({
    experienceBox: {
        defaultMessage: 'Geschenkbox',
    },
    pdfVoucher: {
        defaultMessage: 'PDF-Gutschein',
    },
    productGroupTextPackageDeliveryForDE: {
        defaultMessage: 'Standardlieferzeit 2-5 Werktage | Expresslieferung möglich',
    },
    productGroupTextPackageDeliveryForATAndCH: {
        defaultMessage: 'Lieferzeit 5-6 Werktage',
    },
    productGroupTextInstantDelivery: {
        defaultMessage: 'Sofort verfügbar',
    },
});

const productGroupTextPackageDeliveryForTenants: { [key: string]: { defaultMessage: string } } = {
    [TenantEnum.JS_DE]: messages.productGroupTextPackageDeliveryForDE,
    [TenantEnum.JS_AT]: messages.productGroupTextPackageDeliveryForATAndCH,
    [TenantEnum.MD_DE]: messages.productGroupTextPackageDeliveryForDE,
    [TenantEnum.MD_AT]: messages.productGroupTextPackageDeliveryForATAndCH,
    [TenantEnum.MD_CH]: messages.productGroupTextPackageDeliveryForATAndCH,
};

type ProductGroupItemProps = {
    readonly url: string;
    readonly type?: ProductType | string;
    readonly isProductGroupItemSelected: boolean;
    readonly onChange: (e: ChangeEvent) => void;
};

const ProductGroupItem = ({
    isProductGroupItemSelected,
    onChange,
    type,
    url,
}: ProductGroupItemProps): JSX.Element => {
    const intl = useIntl();
    const { tenant } = useFragmentContext<ProductDetailContext>();
    const { isRedemptionCartFilled } = useRedemptionCart();

    const handleProductItemClick = (_: string, e: ChangeEvent): void => {
        trackProductItemClicked(type === ProductType.BOX ? 'Geschenkbox' : 'PDF-Gutschein');
        onChange(e);
    };

    return (
        <RadioButton
            checked={isProductGroupItemSelected}
            onChange={handleProductItemClick}
            value={url}
            classNames={{
                wrapperClassName: classNames(
                    styles.productGroupItem,
                    'text-decoration-none py-2x',
                    {
                        [styles.disabled]: isRedemptionCartFilled,
                    },
                ),
            }}
            disabled={isRedemptionCartFilled}
            id={url}
            textAlign={CheckboxRadioAlign.Top}
        >
            <Link
                className="d-flex align-items-center text-decoration-none justify-content-between flex-grow-1"
                href={url}
                variant={LinkVariant.Grey}
            >
                <div className="d-flex flex-column flex-grow-1">
                    <strong className={classNames('theme-text-subtitle-text', styles.resetColor)}>
                        {type === ProductType.BOX
                            ? intl.formatMessage(messages.experienceBox)
                            : intl.formatMessage(messages.pdfVoucher)}
                    </strong>
                    <span className="fw-regular">
                        {intl.formatMessage(
                            type === ProductType.BOX
                                ? productGroupTextPackageDeliveryForTenants[tenant]
                                : messages.productGroupTextInstantDelivery,
                        )}
                    </span>
                </div>
                {type === ProductType.BOX ? (
                    <GiftWrappingIcon size={32} className={styles.icon} />
                ) : (
                    <PdfIcon size={32} className={styles.icon} />
                )}
            </Link>
        </RadioButton>
    );
};

export { ProductGroupItem };
